/**
 * User profile setup modal
 * Collects user info: name, billing address, etc.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { saveUserProfile, loadUserProfile, type UserProfile } from '@/lib/userProfile';
import { THEMES, applyTheme, getTheme, loadSavedTheme, saveTheme } from '@/lib/themes';
import { getAuthInstance } from '@/lib/firebase';

interface ProfileSetupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  required?: boolean; // If true, user can't close without completing
}

export function ProfileSetupModal({ open, onOpenChange, required = false }: ProfileSetupModalProps) {
  const [loading, setLoading] = useState(false);
  const [uid, setUid] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    displayName: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    preferredTheme: loadSavedTheme(),
  });

  // Load current user and their profile
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const auth = await getAuthInstance();
        if (auth.currentUser && mounted) {
          const userId = auth.currentUser.uid;
          setUid(userId);
          
          // Load existing profile if any
          const profile = await loadUserProfile(userId);
          if (profile && mounted) {
            setFormData({
              displayName: profile.displayName || '',
              street: profile.billingAddress?.street || '',
              city: profile.billingAddress?.city || '',
              state: profile.billingAddress?.state || '',
              zip: profile.billingAddress?.zip || '',
              country: profile.billingAddress?.country || 'US',
              preferredTheme: profile.preferredTheme || loadSavedTheme(),
            });
          }
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      }
    })();
    return () => { mounted = false; };
  }, [open]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!uid) return alert('User not authenticated');
    
    setLoading(true);
    try {
      const auth = await getAuthInstance();
      const email = auth.currentUser?.email || '';

      const profile: UserProfile = {
        uid,
        email,
        displayName: formData.displayName.trim() || undefined,
        billingAddress: {
          street: formData.street.trim() || undefined,
          city: formData.city.trim() || undefined,
          state: formData.state.trim() || undefined,
          zip: formData.zip.trim() || undefined,
          country: formData.country.trim() || undefined,
        },
        preferredTheme: formData.preferredTheme,
        profileComplete: true,
        updatedAt: new Date().toISOString(),
      };

      await saveUserProfile(profile);
      try {
        saveTheme(formData.preferredTheme);
        applyTheme(getTheme(formData.preferredTheme));
      } catch (themeError) {
        console.warn('Profile saved, but theme update failed:', themeError);
      }
      alert('Profile saved successfully!');
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to save profile:', err);
      alert('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (required) {
      if (!confirm('Profile setup is required. Are you sure you want to skip?')) {
        return;
      }
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={required ? handleClose : onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {required ? 'Complete Your Profile' : 'Edit Profile'}
          </DialogTitle>
          <DialogDescription>
            {required
              ? 'Please provide your information to continue using the app.'
              : 'Update your account and billing information.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="profile-displayName">Display Name *</Label>
            <Input
              id="profile-displayName"
              value={formData.displayName}
              onChange={(e) => setFormData((prev) => ({ ...prev, displayName: e.target.value }))}
              placeholder="Your name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-street">Street Address</Label>
            <Input
              id="profile-street"
              value={formData.street}
              onChange={(e) => setFormData((prev) => ({ ...prev, street: e.target.value }))}
              placeholder="123 Main St"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="profile-city">City</Label>
              <Input
                id="profile-city"
                value={formData.city}
                onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                placeholder="San Francisco"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-state">State/Province</Label>
              <Input
                id="profile-state"
                value={formData.state}
                onChange={(e) => setFormData((prev) => ({ ...prev, state: e.target.value }))}
                placeholder="CA"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="profile-zip">ZIP/Postal Code</Label>
              <Input
                id="profile-zip"
                value={formData.zip}
                onChange={(e) => setFormData((prev) => ({ ...prev, zip: e.target.value }))}
                placeholder="94102"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-country">Country</Label>
              <Input
                id="profile-country"
                value={formData.country}
                onChange={(e) => setFormData((prev) => ({ ...prev, country: e.target.value }))}
                placeholder="US"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-theme">App Theme</Label>
            <Select
              value={formData.preferredTheme}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, preferredTheme: value }))
              }
            >
              <SelectTrigger id="profile-theme">
                <SelectValue placeholder="Choose a theme" />
              </SelectTrigger>
              <SelectContent>
                {Object.values(THEMES).map((theme) => (
                  <SelectItem key={theme.id} value={theme.id}>
                    {theme.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Applies across the app once you save.
            </p>
          </div>

          <DialogFooter>
            {!required && (
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Profile'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
